import { setCurrentLocation } from '@/core/redux/currentLocationSlice';
import { RootState } from '@/core/redux/store';
import { getData } from '@/services/api.service';
import { routePrefix } from '@/utils/routes/routes';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';

interface ThemeData {
  theme_5: {
    home: {
      section_1: {
        banner: string;
        title: string;
        paragraph: string;
        banner_two?: string;
        banner_type?: 'image' | 'video';
        title_color?: string;
        paragraph_color?: string;
      };
    };
  };
}

interface PostCodeData {
  postcode: string;
  id?: string;
  [key: string]: any;
}

const HomeSearchBanner: React.FC<{ themeData: ThemeData }> = ({
  themeData,
}) => {
  const [pinCode, setPinCode] = useState<string>('');
  const [postCodeList, setPostCodeList] = useState<PostCodeData[]>([]);
  const [selectedPostCode, setSelectedPostCode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const headerData = useSelector((state: RootState) => state.common.header);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const rawHtml = themeData?.theme_5?.home?.section_1?.paragraph || '';
  const cleanText = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS: ['br', 'p', 'strong', 'b', 'em'],
  });

  const handlePostcodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!pinCode.trim()) {
      setError('Postcode is required');
      return;
    }

    setIsLoading(true);
    try {
      const response = await getData(`get_shop_by_pincode?pincode=${pinCode}`);

      if (response.status === 200 || response.status === 201) {
        if (response?.data?.success && response?.data?.data?.id) {
          navigate(`${routePrefix}/shop/${response.data.data.id}`);
          dispatch(setCurrentLocation(response.data.data));
        } else {
          setError('No shop found for this postcode');
        }
      }
    } catch (err: any) {
      setError('Area not covered');
    } finally {
      setIsLoading(false);
    }
  };

  const getPostalCode = async () => {
    try {
      const response = await getData(`get_postal_codes?keyword=${pinCode}`);

      if (response.status === 200 || response.status === 201) {
        setPostCodeList(response?.data?.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch postal codes:', err);
      setPostCodeList([]);
    }
  };

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (pinCode && !selectedPostCode) getPostalCode();
    }, 100);

    return () => clearTimeout(debounceTimer);
  }, [pinCode]);

  useEffect(() => {
    if (selectedPostCode) {
      setPinCode(selectedPostCode);
      setPostCodeList([]);
    }
  }, [selectedPostCode]);

  return (
    <div
      className="home_single_search theme5"
      style={{
        background: `var(--primary-color) url(${themeData?.theme_5?.home?.section_1?.banner}) no-repeat top center`,
      }}
    >
      <div className="container b">
        <div className="row">
          {/* Left Column */}
          <div className="col-md-12 col-lg-7">
            <div className="banner-search-wrap">
              <span className="banner-tag"></span>
              <h1
                style={{
                  color: themeData?.theme_5?.home?.section_1?.title_color
                    ? `#${themeData.theme_5.home.section_1.title_color}`
                    : '#ffffff',
                }}
              >
                {themeData?.theme_5?.home?.section_1?.title}
              </h1>
              <p
                className="d-none d-md-block"
                style={{
                  color: themeData?.theme_5?.home?.section_1?.paragraph_color
                    ? `#${themeData.theme_5.home.section_1.paragraph_color}`
                    : '#ffffff',
                }}
              >
                {parse(cleanText)}
                {/* {themeData?.theme_5?.home?.section_1?.paragraph} */}
              </p>
              {headerData?.postal_code_enabled == '1' && (
                <div className="postcodeform-container">
                  <form onSubmit={handlePostcodeSubmit}>
                    <div>
                      {error && (
                        <small className="text-danger postvalidation">
                          {error}
                        </small>
                      )}
                      <div className="postcodeform">
                        <input
                          name="pinCode"
                          type="text"
                          id="postcode-input"
                          placeholder="Enter Postcode"
                          className="ui-autocomplete-input"
                          autoComplete="off"
                          value={pinCode}
                          onChange={(e) => {
                            setPinCode(e.target.value);
                            setError('');
                          }}
                        />
                        <button
                          className="btn hover-btn"
                          id="book_now_btn"
                          type="submit"
                          style={{
                            background: 'rgb(47, 160, 234)',
                            color: '#fff',
                          }}
                          disabled={isLoading}
                        >
                          Search
                        </button>
                        {postCodeList.length > 0 && (
                          <div className="dropdown">
                            {postCodeList.map((data: PostCodeData) => (
                              <div
                                key={data.postcode}
                                className="dropdown-row"
                                onClick={() =>
                                  setSelectedPostCode(data.postcode)
                                }
                              >
                                <i className="fas fa-map-marker-alt"></i>
                                {data.postcode}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Image */}
          <div className="col-lg-5 d-none d-lg-block">
            <div className="heroimg">
              {themeData?.theme_5?.home?.section_1?.banner_two &&
              themeData?.theme_5.home.section_1.banner_type === 'image' ? (
                <img
                  src={themeData?.theme_5.home.section_1.banner_two}
                  alt="Hero Image"
                  className="img-fluid"
                  loading="lazy"
                />
              ) : (
                themeData?.theme_5?.home?.section_1?.banner_two && (
                  <video
                    loop
                    autoPlay
                    muted
                    playsInline
                    className="img-fluid"
                    src={themeData?.theme_5.home.section_1.banner_two}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeSearchBanner;
